import { Link } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowUpRight, ArrowRight, Radio, PlayCircle, X, Eye } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import AdminActions from "@/components/AdminActions";
import ShareButton from "@/components/ShareButton";
import VideoEmbed from "@/components/VideoEmbed";
import { fontClass } from "@/lib/fonts";
import Seo from "@/components/Seo";

function DateBadge({ date }) {
  const d = new Date(date);
  return (
    <div className="w-14 shrink-0 text-center bg-orange-500/10 border border-orange-400/20 rounded-xl py-2" aria-hidden="true">
      <div className="font-display font-bold text-lg text-orange-300 leading-none">{d.getDate()}</div>
      <div className="text-[10px] uppercase tracking-wide text-orange-400 font-bold mt-0.5">
        {d.toLocaleDateString("en-US", { month: "short" })}
      </div>
    </div>
  );
}

export default function News() {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [openVideo, setOpenVideo] = useState(null);
  const [cat, setCat] = useState("All");

  const load = useCallback(() => {
    api.get("/news").then(r => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const cats = useMemo(() => ["All", ...Array.from(new Set(items.map(i => i.category)))], [items]);
  const filtered = cat === "All" ? items : items.filter(i => i.category === cat);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Every update, straight to your inbox.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen pt-28">
      <Seo title="AI News & Updates" description="Every model launch, tool drop, and workflow shift — tracked, filtered, and explained for AI learners." path="/news" />
      <section className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="overline-label mb-4 flex items-center gap-2"><Radio size={13} /> AI Updates</div>
          <h1 className="font-display font-bold tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-7xl text-white">
            AI updates that matter.<br />
            <span className="font-bn-display italic font-normal text-gradient">The rest is noise.</span>
          </h1>
          <p className="text-slate-200 mt-6 max-w-2xl leading-relaxed">
            Every model launch, tool drop, and workflow shift — tracked, filtered, and linked. Subscribers get each update pushed straight to their inbox.
          </p>

          <div className="mt-6"><AdminActions kind="news" onChange={load} /></div>

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`news-cat-${c.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${
                  cat === c
                    ? "bg-white text-[#04121A]"
                    : "bg-white/10 text-slate-200 hover:bg-white/20"
                }`}
              >
                {c}
                <span className="ml-1.5 text-xs text-slate-200">
                  {c === "All" ? items.length : items.filter(i => i.category === c).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl divide-y divide-white/[0.07] overflow-hidden">
          {filtered.map((n, i) => (
            <motion.div
              key={n.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4) }}
              className="p-4 md:p-5 hover:bg-white/5 transition-colors group"
              data-testid={`news-row-${n.id}`}
            >
              <div className="flex items-center gap-4 md:gap-5">
                <DateBadge date={n.published_at} />
                {n.image_url && (
                  <div className="hidden sm:block w-24 h-16 rounded-lg overflow-hidden border border-white/10 shrink-0">
                    <img src={n.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {n.is_article && n.slug ? (
                  <Link to={`/news/${n.slug}`} className="flex-1 min-w-0" data-testid={`news-link-${n.id}`}>
                  <div className="flex items-center gap-2.5 mb-1">
                    <span className="chip">{n.category}</span>
                    {n.source && <span className="text-xs text-slate-200 font-medium">{n.source}</span>}
                    {(n.views || 0) > 0 && (
                      <span className="text-xs text-slate-200 font-medium flex items-center gap-1" data-testid={`news-views-${n.id}`}>
                        <Eye size={12} /> {n.views.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className={`${fontClass(n.title_font)} font-semibold text-white text-base md:text-lg leading-snug group-hover:text-orange-300 transition-colors`}>
                    {n.title}
                  </div>
                  </Link>
                ) : (
                  <a href={n.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0" data-testid={`news-link-${n.id}`}>
                  <div className="flex items-center gap-2.5 mb-1">
                    <span className="chip">{n.category}</span>
                    {n.source && <span className="text-xs text-slate-200 font-medium">{n.source}</span>}
                  </div>
                  <div className={`${fontClass(n.title_font)} font-semibold text-white text-base md:text-lg leading-snug group-hover:text-orange-300 transition-colors`}>
                    {n.title}
                  </div>
                  </a>
                )}
                {n.video_url && (
                  <button
                    onClick={() => setOpenVideo(openVideo === n.id ? null : n.id)}
                    className="text-orange-400 hover:text-orange-300 transition-colors shrink-0"
                    title={openVideo === n.id ? "Close video" : "Watch video"}
                    data-testid={`news-video-toggle-${n.id}`}
                  >
                    {openVideo === n.id ? <X size={20} /> : <PlayCircle size={22} />}
                  </button>
                )}
                <ShareButton url={n.is_article && n.slug ? `/api/share/news/${n.slug}` : n.url} testId={`share-news-${n.id}`} />
                <AdminActions kind="news" item={n} onChange={load} mode="row" />
                <a href={n.is_article && n.slug ? `/news/${n.slug}` : n.url} target={n.is_article ? "_self" : "_blank"} rel="noreferrer" aria-label="Open link">
                  <ArrowUpRight size={18} className="text-slate-200 group-hover:text-orange-400 transition-colors shrink-0" />
                </a>
              </div>
              {openVideo === n.id && n.video_url && (
                <div className="mt-5 max-w-2xl">
                  <VideoEmbed url={n.video_url} />
                </div>
              )}
            </motion.div>
          ))}
          {filtered.length === 0 && (
            <div className="p-12 text-center text-slate-200">No updates yet — check back soon.</div>
          )}
        </div>

        {/* Subscribe CTA */}
        <div className="mt-12 relative bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden p-8 md:p-12">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 85% 0%, rgba(249,115,22,0.2), transparent 55%)" }}
            aria-hidden="true"
          />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight">
                Never miss an <span className="text-orange-400">update.</span>
              </h2>
              <p className="mt-3 text-slate-200 text-sm font-bn">Every new update lands in your inbox the moment it's published.</p>
            </div>
            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3" data-testid="news-subscribe-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder:text-slate-200 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/30 transition-colors"
                data-testid="news-subscribe-email"
              />
              <button type="submit" disabled={busy} className="btn-glow justify-center" data-testid="news-subscribe-submit">
                {busy ? "..." : <>Subscribe <ArrowRight size={14} /></>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
